import cx from 'classnames'
import { useState, useMemo, useEffect, useContext, createElement } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { SanitySimplePortableText } from '@data/sanity/queries/types/content'
import {
  SanityPrintFeeCombination,
  SanityPrintPermission,
  SanityProductPrintConfiguration,
} from '@data/sanity/queries/types/print'
import {
  SanityProductType,
  SanityProductVariantFragment,
} from '@data/sanity/queries/types/product'
import {
  SanityInstitutionItem,
  SanityInstitutionPrintTypes,
} from '@data/sanity/queries/types/institution'
import { SanityShopPrint } from '@data/sanity/queries/types/shop'
import { SanityPrintConfiguratorStrings } from '@data/sanity/queries/types/strings'
import { AttributeInput } from '@data/shopify/storefront/types'
import {
  configurationIdAttributeKey,
  getProductVariantAttributes,
  getShopAttributes,
} from '@lib/shopify/cart'
import { getInstitutionAttributes } from '@lib/shopify/institution'
import { CartContext, CartLineItem } from '@lib/cart'
import { LanguageContext } from '@lib/language'
import {
  getCartItemInstitutionIds,
  getPrintFeeLineItems,
} from '@lib/print/cart'
import {
  getActivePrintFeeCombinations,
  getPrintFeeTotalPrice,
} from '@lib/print/fee'
import {
  ProductGalleryImageWithAreas,
  SlotConfiguration,
  SlotWithConfiguration,
} from '@lib/print/types'
import { StringsContext } from '@lib/strings-context'

import PrintConfiguratorProgressIndicator from './progress-indicator'
import PrintConfiguratorSlot, { PrintConfiguratorSlotProps } from './slot'

interface PrintConfiguratorProps {
  shopId: string
  institution: SanityInstitutionItem
  prints: SanityShopPrint[]
  productType: SanityProductType
  printConfiguration: SanityProductPrintConfiguration[]
  printPermissions: SanityPrintPermission[]
  productGalleryWithPrintAreas: ProductGalleryImageWithAreas[]
  productVariant: SanityProductVariantFragment
  productUrl: string
  printConfiguratorStrings: SanityPrintConfiguratorStrings
  printFeeCombinations: SanityPrintFeeCombination[]
  institutionPrintPermissions?: SanityInstitutionPrintTypes
}

interface PrintConfiguratorSlot {
  component: (props: PrintConfiguratorSlotProps) => JSX.Element
  props: PrintConfiguratorSlotProps
}

const PrintConfigurator = ({
  shopId,
  institution,
  prints,
  printConfiguration,
  printPermissions,
  productGalleryWithPrintAreas,
  productVariant,
  productUrl,
  printConfiguratorStrings,
  printFeeCombinations,
}: PrintConfiguratorProps) => {
  const strings = useContext(StringsContext)
  const { cart, addItemsToCart } = useContext(CartContext)
  const { locale } = useContext(LanguageContext)

  const [addToCartError, setAddToCartError] =
    useState<SanitySimplePortableText | null>(null)
  const [activeSlotIndex, setActiveSlotIndex] = useState<number>(0)
  const [slotsWithConfiguration, setSlotsWithConfigurations] = useState<
    SlotWithConfiguration[]
  >(new Array(printConfiguration.length))

  const slots = useMemo(() => {
    return printConfiguration.map(
      (printConfigurationItem) => printConfigurationItem.slot,
    )
  }, [printConfiguration])

  const activeSlot = useMemo(() => {
    return slotsWithConfiguration[activeSlotIndex]
  }, [activeSlotIndex, slotsWithConfiguration])

  const totalPrice = useMemo(() => {
    const activePrintFeeCombinations = getActivePrintFeeCombinations(
      printFeeCombinations,
      slotsWithConfiguration,
    )
    const printFeeTotalPrice = getPrintFeeTotalPrice(activePrintFeeCombinations)

    return productVariant.price + printFeeTotalPrice
  }, [printFeeCombinations, productVariant.price, slotsWithConfiguration])

  const imagesWithAreas = useMemo(() => {
    const gallery = productGalleryWithPrintAreas.find((gallery) => {
      const option = productVariant.options
        .map((option) => `${option.name}:${option.value}`)
        .find((option) => option === gallery.forOption)

      return gallery.forOption === option
    })

    return gallery?.imagesWithAreas
  }, [productGalleryWithPrintAreas, productVariant])

  const handleAddToCart = async () => {
    setAddToCartError(null)

    // Check if any cart item has a different institution ID from current institution
    const cartItemInstitutionIds = getCartItemInstitutionIds(cart.lineItems)
    const hasCartOtherInstitutions = cartItemInstitutionIds.some(
      (institutionId) => institutionId !== institution.id,
    )

    if (hasCartOtherInstitutions) {
      setAddToCartError(
        printConfiguratorStrings.printConfiguratorDifferentSchoolMessage,
      )
      return
    }

    // Check if the current product variant (configured print product) is already in the cart
    const hasCartProductVariant = cart.lineItems.some(
      (lineItem) => lineItem.id === productVariant.id,
    )

    if (hasCartProductVariant) {
      setAddToCartError(
        printConfiguratorStrings.printConfiguratorExistingVariantInCartMessage,
      )
      return
    }

    const isSuccessful = await addItemsToCart(async () => {
      // Get line item attributes
      const shopAttributes = getShopAttributes(shopId)
      const productVariantAttributes =
        getProductVariantAttributes(productVariant)
      const configurationAttributes: AttributeInput[] = [
        {
          key: `_${configurationIdAttributeKey}`,
          value: uuidv4(),
        },
      ]
      const institutionAttributes = getInstitutionAttributes(
        institution.id,
        institution.name,
      )
      const attributes: AttributeInput[] = [
        ...shopAttributes,
        ...productVariantAttributes,
        ...configurationAttributes,
        ...institutionAttributes,
      ]

      // Get product variant line item
      const productLineItem: CartLineItem = {
        id: productVariant.id,
        quantity: 1,
        attributes,
      }

      // Get fee line items (for logo, image and text prints)
      const feeLineItems = await getPrintFeeLineItems(
        locale,
        printFeeCombinations,
        slotsWithConfiguration,
        attributes,
      )

      return [productLineItem, ...feeLineItems]
    })

    if (!isSuccessful) {
      setAddToCartError(strings.cartAddToCartErrorMessage)
    }
  }

  const handleConfigurationChange = (
    slotId: string,
    newConfiguration: Partial<SlotConfiguration>,
  ) => {
    setSlotsWithConfigurations((currentSlotsWithConfiguration) => {
      return currentSlotsWithConfiguration.map(
        (currentSlotWithConfiguration) => {
          if (currentSlotWithConfiguration._id !== slotId) {
            return currentSlotWithConfiguration
          }

          const slotWithConfiguration: SlotWithConfiguration = {
            ...currentSlotWithConfiguration,
            configuration: {
              ...currentSlotWithConfiguration.configuration,
              ...newConfiguration,
            },
          }

          return slotWithConfiguration
        },
      )
    })
  }

  // Update slots with configuration when print configuration changes
  useEffect(() => {
    const newSlotsWithConfigurations = printConfiguration.map(
      (printConfigurationItem) => {
        const slotWithConfiguration: SlotWithConfiguration = {
          ...printConfigurationItem.slot,
          configuration: {
            freetextEnabled: 'yes',
            freetext: '',
            freetextColor: 'light',
            logoEnabled: 'yes',
            logo: null,
            imageEnabled: 'yes',
            image: null,
          },
        }

        return slotWithConfiguration
      },
    )

    setSlotsWithConfigurations(newSlotsWithConfigurations)
  }, [printConfiguration])

  const printConfiguratorSlots = useMemo(() => {
    return slotsWithConfiguration.map((slotWithConfiguration, index) => {
      const permissions = printPermissions.find(
        (permissions) => permissions.slot?._id == slotWithConfiguration?._id,
      )
      const imageWithAreas = imagesWithAreas?.find(
        (images) => images.slotType === slotWithConfiguration?.type,
      )
      const showAddToCartButton = printConfiguration.length - 1 === index
      const printConfiguratorSlot: PrintConfiguratorSlot = {
        component: PrintConfiguratorSlot,
        props: {
          slotsWithConfiguration,
          activeSlotIndex: index,
          prints,
          printFeeCombinations,
          totalPrice,
          permissions,
          imageWithAreas,
          showAddToCartButton,
          addToCartError,
          productVariant,
          printConfiguratorStrings,
          onConfigurationChange: handleConfigurationChange,
          onNextClick: () => {
            setActiveSlotIndex(index + 1)
          },
          onAddToCartClick: handleAddToCart,
        },
      }

      return printConfiguratorSlot
    })
  }, [
    addToCartError,
    handleAddToCart,
    handleConfigurationChange,
    imagesWithAreas,
    printConfiguration,
    printConfiguratorStrings,
    printFeeCombinations,
    printPermissions,
    prints,
    productVariant,
    slotsWithConfiguration,
    totalPrice,
  ])

  return (
    <div className="container xs:py-8 md:py-16 space-y-8">
      <PrintConfiguratorProgressIndicator
        slots={slots}
        activeSlotIndex={activeSlotIndex}
        productUrl={productUrl}
        firstStepLabel={
          printConfiguratorStrings.printConfiguratorFirstStepLabel
        }
        setActiveSlotIndex={setActiveSlotIndex}
      />

      <div className="flex items-start overflow-hidden">
        {printConfiguratorSlots.map((printConfiguratorSlot, index) => {
          const element = createElement(
            printConfiguratorSlot.component,
            printConfiguratorSlot.props,
          )

          return (
            <div
              key={index}
              className={cx('transition-all duration-300', {
                'z-10': index === activeSlotIndex,
              })}
              style={{
                flex: '0 0 100%',
                transform: `translateX(${-activeSlotIndex * 100}%)`,
              }}
            >
              {element}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default PrintConfigurator
